import { Button } from '@heroui/react'
import { useCurrentUser } from '@qse/api'
import type { User } from '@qse/constants'
import { FormikControl } from '@qse/ui'
import { Form, Formik } from 'formik'
import { QRCodeSVG } from 'qrcode.react'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { authService, use2FA } from '../logic'

interface TwoFAScreenProps {
  onSuccess?: (response: User) => void
  onTOTPSetupRequired?: (totpSetup: any) => void
}
const Setup2FA: React.FC<TwoFAScreenProps> = ({ onSuccess, onTOTPSetupRequired }) => {
  const { handleTOTP, handleDisableTOTP, totpValidationSchema } = use2FA()

  const [qrCodeUrl, setQrCodeUrl] = useState<string>('')
  const [isFirstLogin, setIsFirstLogin] = useState<boolean>(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { data: user } = useCurrentUser()

  useEffect(() => {
    const state = location.state as { isFirstLogin?: boolean; totpSetup?: any }

    setIsFirstLogin(state?.isFirstLogin || false)

    if (state?.totpSetup) {
      console.log('2FA Setup - Using totpSetup from state:', state.totpSetup)
      setQrCodeUrl(state.totpSetup.qrCodeUrl || '')

      // Call onTOTPSetupRequired callback if provided
      onTOTPSetupRequired?.(state.totpSetup)

      return
    }

    // Check if user was redirected here due to mfaStatus being null
    if (user?.mfaStatus === null) {
      console.log('2FA Setup - User has mfaStatus null, generating TOTP setup')

      const generateTOTPSetup = async () => {
        try {
          const totpSetup = await authService.setupTOTP()

          setQrCodeUrl(totpSetup.qrCodeUrl || '')
          console.log('2FA Setup - Generated TOTP setup for null mfaStatus:', totpSetup)
        } catch (error) {
          console.error('2FA Setup - Error generating TOTP setup:', error)
          // If TOTP setup fails, redirect to login
          navigate('/auth/login', { replace: true })
        }
      }

      generateTOTPSetup()

      return
    }

    // If no totpSetup in state and user doesn't need MFA setup, redirect to login
    console.log(
      '2FA Setup - No totpSetup in state and user does not need MFA setup, redirecting to login',
    )
    navigate('/auth/login', { replace: true })
  }, [location.state, onTOTPSetupRequired, navigate, user])

  const disableTOTPHandler = () => {
    handleDisableTOTP(isFirstLogin, user)
  }

  return (
    <div className="h-full">
      <div className="flex items-center h-[calc(100vh-72px)] w-full lg:pl-[110px] lg:max-w-full max-w-[440px] mx-auto">
        <div className="lg:w-[440px] w-full p-8 lg:p-0 flex flex-col gap-11">
          <div className="flex flex-col gap-2">
            <h1 className="text-[28px] font-medium">Two-Factor Authentication Setup</h1>
            <p className="text-[15px]">Secure your account with an authenticator app</p>

            <ul className="text-sm list-disc pl-4 space-y-1">
              <li>
                Install an authenticator app on your mobile device (Google Authenticator, Authy, or
                Microsoft Authenticator)
              </li>
              <li>Open the app and look for "Add account" or "Scan QR code"</li>
              <li>Scan the QR code below or enter the secret code manually</li>
            </ul>
          </div>

          <div className="flex justify-center gap-4 flex-col mx-auto text-center">
            {qrCodeUrl ? (
              <div className="flex justify-center">
                <div className="w-[126px] h-[126px] bg-white rounded-lg flex items-center justify-center p-2">
                  <QRCodeSVG includeMargin={false} level="H" size={110} value={qrCodeUrl} />
                </div>
              </div>
            ) : (
              <div className="w-[126px] h-[126px] bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-gray-500 text-xs">No QR Code Available</span>
              </div>
            )}
          </div>

          <Formik
            initialValues={{ code: '' }}
            validationSchema={totpValidationSchema}
            onSubmit={values =>
              handleTOTP(values, {
                onSuccess,
              })
            }
          >
            {({ isSubmitting, isValid, dirty }) => (
              <Form>
                <div className="flex flex-col gap-[30px]">
                  <div>
                    <p className="block pb-2 font-medium text-sm">
                      Enter the 6-digit code from your authenticator app
                    </p>
                    <FormikControl
                      control="text"
                      label="Verification Code"
                      name="code"
                      placeholder="e.g. 123456"
                      radius="sm"
                      size="md"
                      variant="bordered"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <Button
                    className="w-full mt-2 font-medium"
                    color="primary"
                    isDisabled={!isValid || !dirty}
                    isLoading={isSubmitting}
                    radius="sm"
                    type="submit"
                  >
                    Verify & Complete Setup
                  </Button>
                  <Button
                    className="w-full font-medium"
                    radius="sm"
                    type="button"
                    variant={isFirstLogin ? 'bordered' : 'light'}
                    onPress={disableTOTPHandler}
                  >
                    Skip
                  </Button>
                </div>
              </Form>
            )}
          </Formik>

          <div className="text-center space-y-3">
            <p className="text-xs text-gray-500">
              You can always enable 2FA later in your account settings for enhanced security.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Setup2FA
